import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { FinanceProvider } from './context/FinanceContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Expenses from './pages/Expenses';
import Investments from './pages/Investments';
import BalanceSheet from './pages/BalanceSheet';
import Login from './pages/Login';
import { Database, ExternalLink, Settings, Loader2 } from 'lucide-react';

const AuthenticatedApp: React.FC = () => {
  const { user, isLoading, configError, authPhase, isRecoveryMode } = useAuth();

  const phaseLabels: Record<string, string> = {
    'IDLE': 'Waking up systems...',
    'INITIALIZING': 'Connecting to network...',
    'FETCHING_SESSION': 'Validating session...',
    'SYNCING_PROFILE': 'Synchronizing profile...',
    'READY': 'Finalizing UI...'
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-6">
        <div className="flex flex-col items-center gap-6 max-w-xs w-full text-center">
          <div className="relative">
             <div className="w-16 h-16 border-4 border-slate-100 rounded-2xl animate-pulse"></div>
             <Loader2 className="w-8 h-8 text-blue-600 absolute inset-0 m-auto animate-spin" />
          </div>
          <div className="space-y-1">
            <h2 className="text-sm font-black text-slate-900 tracking-tight uppercase">Wealthy</h2>
            <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em] animate-pulse">
              {phaseLabels[authPhase] || 'Loading...'}
            </p>
          </div>
          <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden">
             <div className="h-full bg-blue-600 animate-progress origin-left"></div>
          </div>
        </div>
      </div>
    );
  }

  if (configError) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-xl shadow-slate-200 border border-slate-100 p-8">
          <div className="bg-amber-50 text-amber-600 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Database size={32} />
          </div>
          <h1 className="text-xl font-black text-slate-900 mb-2">Connection Required</h1>
          <p className="text-slate-500 text-sm mb-8 leading-relaxed">
            Your application is deployed, but it's not connected to your Supabase database yet.
          </p>
          
          <div className="bg-slate-50 rounded-2xl p-6 text-left mb-8 space-y-4">
            <h2 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <Settings size={12} /> Steps to Fix
            </h2>
            <ol className="text-xs text-slate-600 space-y-3">
              <li className="flex gap-2">
                <span className="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">1</span>
                <span>Go to <b>Cloudflare Dashboard</b> &gt; <b>Workers & Pages</b></span>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">2</span>
                <span>Select this project &gt; <b>Settings</b> &gt; <b>Environment Variables</b></span>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">3</span>
                <span>Add <code>SUPABASE_URL</code> and <code>SUPABASE_ANON_KEY</code></span>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">4</span>
                <span>Trigger a <b>new deployment</b> for changes to take effect</span>
              </li>
            </ol>
          </div>

          <a 
            href="https://supabase.com/dashboard" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-full bg-slate-900 text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 hover:bg-slate-800 transition-all shadow-lg shadow-slate-200"
          >
            Supabase Dashboard <ExternalLink size={16} />
          </a>
        </div>
      </div>
    );
  }

  if (!user || isRecoveryMode) {
    return <Login />;
  }

  return (
    <FinanceProvider>
      <Layout>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/expenses" element={<Expenses />} />
          <Route path="/investments" element={<Investments />} />
          <Route path="/balance-sheet" element={<BalanceSheet />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </FinanceProvider>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <AuthenticatedApp />
      </Router>
    </AuthProvider>
  );
};

export default App;